/**
 * Writing to a shared folder (P1-F4): uploads, new folders, delete, rename, and transfers within a
 * shared folder or between it and My Files (E8). `JobService/lambda/code/python/shares/files_api.py`
 * and `transfers.py` in omicscloud-cdk; every route is `/v1/shares/{id}/files/{op}`.
 *
 * Paths are relative to the shared folder: `a/b.txt` for a file, `a/` for a folder, `''` for the
 * shared folder itself (only as a destination). Only Can edit (and the owner) may call the write
 * routes; a viewer gets 403 `insufficient_access`.
 *
 * These are user actions, so they go through {@link apiCallWithAuthCheck}: an expired session opens
 * the sign-in dialog, as for every other file operation. None of them runs when the Files panel
 * loads.
 */
import {API} from 'aws-amplify'
import {apiCallWithAuthCheck} from '../auth/core/AuthErrorInterceptor'

const apiName: string = process.env.REACT_APP_API_NAME || 'JobService'
const enc = encodeURIComponent

const filesRoute = (shareId: string, op: string) => `v1/shares/${enc(shareId)}/files/${op}`

const post = <T>(shareId: string, op: string, body: object): Promise<T> =>
  apiCallWithAuthCheck(() => API.post(apiName, filesRoute(shareId, op), {body}))

/** Uploads up to this size go in one presigned POST; larger ones in parts. */
export const SHARE_SINGLE_UPLOAD_MAX_BYTES = 16 * 1024 * 1024
/** `multipart-parts` signs at most this many part numbers per call. */
export const SHARE_MAX_PARTS_PER_CALL = 100
/** A transfer (move or copy) moves at most this many objects (P1-B6). */
export const SHARE_TRANSFER_MAX_OBJECTS = 1000
/** ...and at most this many bytes. */
export const SHARE_TRANSFER_MAX_BYTES = 20 * 1024 ** 3

/** `upload-url`: a presigned POST. Send `fields` exactly as returned, then the file. */
export interface SharePresignedPost {
  url: string
  fields: Record<string, string>
  maxBytes: number
  expiresIn: number
}

export const requestShareUploadUrl = (shareId: string, path: string): Promise<SharePresignedPost> =>
  post(shareId, 'upload-url', {path})

export const initiateShareMultipart = (
  shareId: string,
  path: string,
  contentType?: string
): Promise<{uploadId: string; path: string}> =>
  post(shareId, 'multipart-initiate', contentType ? {path, contentType} : {path})

/** Presigned PUT URLs for `partNumbers` (at most {@link SHARE_MAX_PARTS_PER_CALL}), by number. */
export const requestSharePartUrls = (
  shareId: string,
  path: string,
  uploadId: string,
  partNumbers: number[]
): Promise<{urls: Record<string, string>; expiresIn: number}> =>
  post(shareId, 'multipart-parts', {path, uploadId, partNumbers})

export interface ShareUploadedPart {
  PartNumber: number
  ETag: string
}

export const completeShareMultipart = (
  shareId: string,
  path: string,
  uploadId: string,
  parts: ShareUploadedPart[]
): Promise<{path: string}> => post(shareId, 'multipart-complete', {path, uploadId, parts})

export const abortShareMultipart = (
  shareId: string,
  path: string,
  uploadId: string
): Promise<{path: string; aborted: boolean}> => post(shareId, 'multipart-abort', {path, uploadId})

/** `path` ends with `/`. */
export const createShareFolder = (shareId: string, path: string): Promise<{path: string}> =>
  post(shareId, 'folder', {path})

/** Files and folders (ending in `/`) inside the shared folder; at most 100 paths per call. */
export const deleteShareItems = (
  shareId: string,
  paths: string[]
): Promise<{deleted: number; failed: string[]}> => post(shareId, 'delete', {paths})

/** Starts a background rename of `path` (a file, or a folder ending in `/`) to `newName`. */
export const renameShareItem = (
  shareId: string,
  path: string,
  newName: string
): Promise<{operationId: string}> => post(shareId, 'rename', {path, newName})

/** `share` is the shared folder; `mine` is the caller's own My Files. One side must be `share`. */
export type ShareTransferSide = 'share' | 'mine'
export type ShareTransferMode = 'move' | 'copy'

export interface ShareTransferRequest {
  mode: ShareTransferMode
  from: ShareTransferSide
  to: ShareTransferSide
  /** Relative to the `from` side: files, or folders ending in `/`. At most 100. */
  sources: string[]
  /** A folder of the `to` side, ending in `/`, or `''` for its top. */
  destination: string
}

/** A dry run's answer: what the transfer would move, and which destinations already exist. */
export interface ShareTransferPlan {
  objects: number
  bytes: number
  /**
   * Destination keys that already exist, relative to their owner's space (so a shared folder's
   * keys start with its `folderPath`). At most 50.
   */
  conflicts: string[]
}

export const dryRunShareTransfer = (
  shareId: string,
  request: ShareTransferRequest
): Promise<ShareTransferPlan> => post(shareId, 'transfer', {...request, dryRun: true})

export interface ShareTransferStarted {
  operationId: string
  objects: number
  bytes: number
  status: ShareOperationStatus
}

export const startShareTransfer = (
  shareId: string,
  request: ShareTransferRequest
): Promise<ShareTransferStarted> => post(shareId, 'transfer', request)

export type ShareOperationStatus = 'queued' | 'running' | 'succeeded' | 'failed'

/** `GET /v1/shares/{id}/operations/{operationId}`: a rename or transfer running in the background. */
export interface ShareOperation {
  operationId: string
  status: ShareOperationStatus
  mode?: ShareTransferMode
  from?: ShareTransferSide
  to?: ShareTransferSide
  sources?: string[]
  destination?: string
  objects?: number
  bytes?: number
  /** Objects copied so far. */
  done?: number
  /** Keys that failed, relative to their owner's space (at most 100). */
  failed?: string[]
  /** On failure, the keys that were copied anyway (a move deletes nothing unless all copied). */
  copied?: string[]
  /** On failure, the error code (`access_lost`, `source_not_found`, `internal_error`, …). */
  error?: string
  createdAt?: string
  updatedAt?: string
}

export const getShareOperation = (shareId: string, operationId: string): Promise<ShareOperation> =>
  apiCallWithAuthCheck(() =>
    API.get(apiName, `v1/shares/${enc(shareId)}/operations/${enc(operationId)}`, {})
  )
